import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { enterpriseAPI, hardeningAPI, globalCampaignAPI } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Link } from 'react-router-dom';
import {
  Send,
  CheckCircle,
  Mail,
  MousePointer,
  AlertTriangle,
  XCircle,
  ShieldOff,
  Globe,
  Zap,
  Activity,
  TrendingUp,
  ArrowRight,
  BarChart3,
} from 'lucide-react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
} from 'recharts';

export default function DashboardPage() {
  const { currentWorkspace } = useAuth();
  const [monitor, setMonitor] = useState(null);
  const [enterprise, setEnterprise] = useState(null);
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [monitorLoading, setMonitorLoading] = useState(true);

  useEffect(() => {
    loadFastData();
    loadSlowData();
  }, []);

  // Phase 1: fast endpoints — renders dashboard immediately
  const loadFastData = async () => {
    setLoading(true);
    try {
      const [entRes, campRes] = await Promise.all([
        enterpriseAPI.dashboard(),
        globalCampaignAPI.list(),
      ]);
      setEnterprise(entRes.data);
      setCampaigns(campRes.data || []);
    } catch (error) {
      console.error('Dashboard hızlı veri hatası:', error);
    } finally {
      setLoading(false);
    }
  };

  // Phase 2: slow aggregation — fills in event stats after render
  const loadSlowData = async () => {
    setMonitorLoading(true);
    try {
      const monRes = await hardeningAPI.productionMonitor();
      setMonitor(monRes.data);
    } catch (error) {
      console.error('Production monitor hatası:', error);
    } finally {
      setMonitorLoading(false);
    }
  };

  const ts = monitor?.total_stats || {};
  const dom = enterprise?.domains || {};
  const healthDist = enterprise?.health_distribution || {};

  const domainHealthAvg = (() => {
    const total = (healthDist.healthy || 0) + (healthDist.warning || 0) + (healthDist.danger || 0) + (healthDist.paused || 0);
    if (!total) return 0;
    const score = ((healthDist.healthy || 0) * 100 + (healthDist.warning || 0) * 60 + (healthDist.danger || 0) * 20) / total;
    return Math.round(score);
  })();

  const LOADING_PLACEHOLDER = '...';

  const statCards = [
    {
      title: 'Toplam Email Gönderildi',
      value: enterprise?.total_emails_sent ?? 0,
      icon: Send,
      color: 'text-primary',
      bgColor: 'bg-primary/10',
      borderColor: 'border-primary/20',
      testId: 'stat-total-sent',
      slow: false,
    },
    {
      title: 'Teslim Edildi',
      value: ts.delivered ?? LOADING_PLACEHOLDER,
      icon: CheckCircle,
      color: 'text-emerald-500',
      bgColor: 'bg-emerald-500/10',
      borderColor: 'border-emerald-500/20',
      testId: 'stat-delivered',
      slow: true,
    },
    {
      title: 'Açıldı',
      value: ts.opened ?? LOADING_PLACEHOLDER,
      icon: Mail,
      color: 'text-blue-500',
      bgColor: 'bg-blue-500/10',
      borderColor: 'border-blue-500/20',
      testId: 'stat-opened',
      slow: true,
    },
    {
      title: 'Tıklandı',
      value: ts.clicked ?? LOADING_PLACEHOLDER,
      icon: MousePointer,
      color: 'text-violet-500',
      bgColor: 'bg-violet-500/10',
      borderColor: 'border-violet-500/20',
      testId: 'stat-clicked',
      slow: true,
    },
    {
      title: 'Bounce',
      value: ts.bounced ?? LOADING_PLACEHOLDER,
      icon: AlertTriangle,
      color: 'text-amber-500',
      bgColor: 'bg-amber-500/10',
      borderColor: 'border-amber-500/20',
      testId: 'stat-bounced',
      slow: true,
    },
    {
      title: 'Dropped',
      value: ts.dropped ?? LOADING_PLACEHOLDER,
      icon: XCircle,
      color: 'text-red-400',
      bgColor: 'bg-red-400/10',
      borderColor: 'border-red-400/20',
      testId: 'stat-dropped',
      slow: true,
    },
    {
      title: 'Suppression Listesi',
      value: monitor?.suppression_size ?? enterprise?.global_suppression ?? LOADING_PLACEHOLDER,
      icon: ShieldOff,
      color: 'text-rose-500',
      bgColor: 'bg-rose-500/10',
      borderColor: 'border-rose-500/20',
      testId: 'stat-suppression',
      slow: false,
    },
    {
      title: 'Aktif Sender Domain',
      value: dom.active ?? 0,
      icon: Globe,
      color: 'text-cyan-500',
      bgColor: 'bg-cyan-500/10',
      borderColor: 'border-cyan-500/20',
      testId: 'stat-domains',
      slow: false,
    },
    {
      title: 'Aktif Kampanya',
      value: monitor?.active_campaigns ?? campaigns.filter(c => c.status === 'running').length,
      icon: Zap,
      color: 'text-orange-500',
      bgColor: 'bg-orange-500/10',
      borderColor: 'border-orange-500/20',
      testId: 'stat-active-campaigns',
      slow: false,
    },
    {
      title: 'Domain Sağlık Skoru',
      value: `${domainHealthAvg}%`,
      icon: Activity,
      color: domainHealthAvg >= 70 ? 'text-green-500' : domainHealthAvg >= 50 ? 'text-amber-500' : 'text-red-500',
      bgColor: domainHealthAvg >= 70 ? 'bg-green-500/10' : domainHealthAvg >= 50 ? 'bg-amber-500/10' : 'bg-red-500/10',
      borderColor: domainHealthAvg >= 70 ? 'border-green-500/20' : domainHealthAvg >= 50 ? 'border-amber-500/20' : 'border-red-500/20',
      testId: 'stat-domain-health',
      slow: false,
    },
  ];

  const funnelData = [
    { name: 'İşlendi', value: ts.processed ?? 0, color: '#6366f1' },
    { name: 'Teslim', value: ts.delivered ?? 0, color: '#10b981' },
    { name: 'Açıldı', value: ts.opened ?? 0, color: '#3b82f6' },
    { name: 'Tıklandı', value: ts.clicked ?? 0, color: '#8b5cf6' },
    { name: 'Bounce', value: ts.bounced ?? 0, color: '#f59e0b' },
    { name: 'Spam', value: ts.spam ?? 0, color: '#ef4444' },
  ];

  if (loading) {
    return (
      <div className="space-y-6 animate-pulse" data-testid="dashboard-loading">
        <div className="h-10 bg-muted rounded w-48" />
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
          {[1, 2, 3, 4, 5].map((i) => (
            <div key={i} className="h-28 bg-muted rounded-lg" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="dashboard-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-3xl font-bold">Dashboard</h1>
          <p className="text-muted-foreground mt-1">
            {currentWorkspace?.name} — Email Platformu Genel Bakış
          </p>
        </div>
        <Link
          to="/global-mail"
          className="flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-lg text-sm font-medium hover:bg-primary/90 transition-colors"
          data-testid="new-campaign-btn"
        >
          <Zap className="w-4 h-4" />
          Global Mail
        </Link>
      </div>

      {/* Stats Grid — 5 columns on large */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
        {statCards.map((stat) => (
          <Card
            key={stat.title}
            className={`border ${stat.borderColor}`}
            data-testid={stat.testId}
          >
            <CardContent className="p-5">
              <div className="flex items-start justify-between">
                <div className="min-w-0">
                  <p className="text-muted-foreground text-xs truncate">{stat.title}</p>
                  {stat.slow && monitorLoading ? (
                    <div className="h-8 w-20 bg-muted animate-pulse rounded mt-1.5" />
                  ) : (
                    <p className="font-heading text-2xl font-bold mt-1.5">
                      {typeof stat.value === 'number' ? stat.value.toLocaleString('tr-TR') : stat.value}
                    </p>
                  )}
                </div>
                <div className={`p-2.5 rounded-lg ${stat.bgColor} flex-shrink-0 ml-2`}>
                  <stat.icon className={`w-4 h-4 ${stat.color}`} />
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Email Funnel Chart */}
        <Card className="border-border">
          <CardHeader>
            <CardTitle className="font-heading flex items-center gap-2">
              <BarChart3 className="w-5 h-5 text-primary" />
              Email Performans Hunisi
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-72">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={funnelData} margin={{ top: 4, right: 4, left: -20, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                  <XAxis
                    dataKey="name"
                    tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 12 }}
                    stroke="hsl(var(--border))"
                  />
                  <YAxis
                    tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 12 }}
                    stroke="hsl(var(--border))"
                  />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: 'hsl(var(--card))',
                      border: '1px solid hsl(var(--border))',
                      borderRadius: '8px',
                      fontSize: '12px',
                    }}
                    formatter={(value) => [value.toLocaleString('tr-TR'), '']}
                  />
                  <Bar dataKey="value" radius={[4, 4, 0, 0]}>
                    {funnelData.map((entry, index) => (
                      <Cell key={index} fill={entry.color} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        {/* Domain Health Distribution */}
        <Card className="border-border">
          <CardHeader>
            <CardTitle className="font-heading flex items-center gap-2">
              <Globe className="w-5 h-5 text-primary" />
              Domain Sağlık Dağılımı
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {[
              { label: 'Sağlıklı', key: 'healthy', color: 'bg-emerald-500', textColor: 'text-emerald-500' },
              { label: 'Uyarı', key: 'warning', color: 'bg-amber-500', textColor: 'text-amber-500' },
              { label: 'Tehlikeli', key: 'danger', color: 'bg-red-500', textColor: 'text-red-500' },
              { label: 'Duraklatıldı', key: 'paused', color: 'bg-muted-foreground', textColor: 'text-muted-foreground' },
            ].map(({ label, key, color, textColor }) => {
              const count = healthDist[key] || 0;
              const total = dom.total || 1;
              const pct = Math.round((count / total) * 100);
              return (
                <div key={key} className="space-y-1">
                  <div className="flex justify-between text-sm">
                    <span className={`font-medium ${textColor}`}>{label}</span>
                    <span className="text-muted-foreground">{count} domain ({pct}%)</span>
                  </div>
                  <div className="h-2 rounded-full bg-muted overflow-hidden">
                    <div
                      className={`h-full rounded-full ${color} transition-all duration-700`}
                      style={{ width: `${pct}%` }}
                    />
                  </div>
                </div>
              );
            })}

            <div className="mt-4 pt-4 border-t border-border grid grid-cols-2 gap-3">
              <div className="text-center p-3 rounded-lg bg-muted/40">
                <p className="text-2xl font-bold font-heading">{dom.total || 0}</p>
                <p className="text-xs text-muted-foreground mt-0.5">Toplam Domain</p>
              </div>
              <div className="text-center p-3 rounded-lg bg-muted/40">
                <p className="text-2xl font-bold font-heading">{(dom.total_capacity || 0).toLocaleString('tr-TR')}</p>
                <p className="text-xs text-muted-foreground mt-0.5">Günlük Kapasite</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Recent Global Campaigns */}
      {campaigns.length > 0 && (
        <Card className="border-border">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="font-heading flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-primary" />
                Son Kampanyalar
              </CardTitle>
              <Link
                to="/global-mail"
                className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground transition-colors"
              >
                Tümünü Gör <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {campaigns.slice(0, 6).map((c) => {
                const statusConfig = {
                  completed: { label: 'Tamamlandı', cls: 'bg-emerald-500/10 text-emerald-500 border-emerald-500/20' },
                  running: { label: 'Çalışıyor', cls: 'bg-amber-500/10 text-amber-500 border-amber-500/20' },
                  paused: { label: 'Duraklatıldı', cls: 'bg-blue-500/10 text-blue-500 border-blue-500/20' },
                  stopped: { label: 'Durduruldu', cls: 'bg-red-500/10 text-red-500 border-red-500/20' },
                  created: { label: 'Hazır', cls: 'bg-muted text-muted-foreground border-border' },
                };
                const sc = statusConfig[c.status] || statusConfig.created;
                const pct = c.planned > 0 ? Math.round((c.sent_count / c.planned) * 100) : 0;
                return (
                  <div
                    key={c.id}
                    className="flex items-center justify-between p-3 rounded-lg bg-muted/30 hover:bg-muted/50 transition-colors"
                    data-testid={`campaign-row-${c.id}`}
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                        <Send className="w-4 h-4 text-primary" />
                      </div>
                      <div className="min-w-0">
                        <p className="text-sm font-medium truncate">{c.name}</p>
                        <p className="text-xs text-muted-foreground">
                          {(c.sent_count || 0).toLocaleString('tr-TR')} / {(c.planned || 0).toLocaleString('tr-TR')} email
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3 flex-shrink-0">
                      <span className="text-xs text-muted-foreground hidden sm:block">{pct}%</span>
                      <span className={`px-2 py-0.5 text-xs font-medium rounded-md border ${sc.cls}`}>
                        {sc.label}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Quick Links */}
      <Card className="border-border">
        <CardHeader>
          <CardTitle className="font-heading">Hızlı Erişim</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              { to: '/global-mail', icon: Zap, label: 'Kampanya Oluştur', color: 'bg-primary/10', iconColor: 'text-primary' },
              { to: '/email-cleaner', icon: CheckCircle, label: 'Email Temizle', color: 'bg-emerald-500/10', iconColor: 'text-emerald-500' },
              { to: '/sender-domains', icon: Globe, label: 'Domain Yönet', color: 'bg-cyan-500/10', iconColor: 'text-cyan-500' },
              { to: '/production-monitor', icon: Activity, label: 'Production Monitor', color: 'bg-violet-500/10', iconColor: 'text-violet-500' },
            ].map(({ to, icon: Icon, label, color, iconColor }) => (
              <Link
                key={to}
                to={to}
                className="flex items-center justify-between p-4 rounded-lg border border-border hover:border-primary/50 hover:bg-primary/5 transition-all"
                data-testid={`quick-link-${to.replace('/', '')}`}
              >
                <div className="flex items-center gap-3">
                  <div className={`p-2 rounded-lg ${color}`}>
                    <Icon className={`w-5 h-5 ${iconColor}`} />
                  </div>
                  <span className="font-medium text-sm">{label}</span>
                </div>
                <ArrowRight className="w-4 h-4 text-muted-foreground" />
              </Link>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
